import { useState, useEffect } from 'react';
import { FaTimes, FaFilePdf, FaDownload, FaTrash, FaUpload, FaSpinner, FaInfoCircle, FaHistory, FaPaperPlane } from 'react-icons/fa';
import { documentosAPI, statusAPI } from '../services/api';
import { formatarData } from '../utils/validators';
import Toast from './Toast';

const DocumentsModal = ({ cliente, onClose }) => {
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [descricoes, setDescricoes] = useState({});
  const [toast, setToast] = useState(null);

  // Status
  const [statusAtual, setStatusAtual] = useState(null);
  const [historicoStatus, setHistoricoStatus] = useState([]);
  const [novoStatus, setNovoStatus] = useState('');
  const [salvandoStatus, setSalvandoStatus] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [showHistoricoModal, setShowHistoricoModal] = useState(false);

  useEffect(() => {
    if (cliente) {
      carregarDocumentos();
      carregarStatus();
    }
  }, [cliente]);

  const carregarStatus = async () => {
    try {
      setLoadingStatus(true);
      const { data } = await statusAPI.buscar(cliente.id);
      setStatusAtual(data.status_atual);
      setHistoricoStatus(data.historico);
    } catch (error) {
      console.error('Erro ao carregar status:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleSalvarStatus = async () => {
    if (!novoStatus.trim()) return;

    try {
      setSalvandoStatus(true);
      await statusAPI.criar(cliente.id, novoStatus.trim());
      setNovoStatus('');
      await carregarStatus();
      setToast({ message: 'Status atualizado com sucesso!', type: 'success' });
    } catch (error) {
      console.error('Erro ao salvar status:', error);
      setToast({ message: 'Erro ao salvar status', type: 'error' });
    } finally {
      setSalvandoStatus(false);
    }
  };

  const handleDeletarStatus = async (statusLog) => {
    if (!window.confirm('Deseja realmente excluir este registro de status?')) {
      return;
    }

    try {
      await statusAPI.deletar(statusLog.id);
      await carregarStatus();
      setToast({ message: 'Registro de status excluído!', type: 'success' });
    } catch (error) {
      console.error('Erro ao excluir status:', error);
      setToast({ message: 'Erro ao excluir registro de status', type: 'error' });
    }
  };

  const carregarDocumentos = async () => {
    try {
      setLoading(true);
      const { data } = await documentosAPI.listar(cliente.id);
      setDocumentos(data);
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
      setToast({ message: 'Erro ao carregar documentos', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const pdfFiles = files.filter((file) => file.type === 'application/pdf');

    if (pdfFiles.length !== files.length) {
      setToast({ message: 'Apenas arquivos PDF são permitidos', type: 'error' });
    }

    setSelectedFiles(pdfFiles);

    // Inicializar descrições vazias
    const newDescricoes = {};
    pdfFiles.forEach((file, index) => {
      newDescricoes[index] = '';
    });
    setDescricoes(newDescricoes);
  };

  const handleDescricaoChange = (index, value) => {
    setDescricoes((prev) => ({ ...prev, [index]: value }));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setToast({ message: 'Selecione pelo menos um arquivo PDF', type: 'error' });
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append('files', file);
      });

      // Adicionar descrições
      Object.values(descricoes).forEach((desc) => {
        formData.append('descricoes', desc);
      });

      await documentosAPI.upload(cliente.id, formData);

      // Limpar seleção
      setSelectedFiles([]);
      setDescricoes({});

      // Recarregar documentos
      await carregarDocumentos();

      setToast({ message: 'Documentos enviados com sucesso!', type: 'success' });
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      setToast({ message: 'Erro ao fazer upload dos documentos', type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (documento) => {
    try {
      const response = await documentosAPI.download(documento.id);

      // Criar link temporário para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', documento.nome_arquivo);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      setToast({ message: 'Erro ao fazer download do documento', type: 'error' });
    }
  };

  const handleDelete = async (documento) => {
    if (!window.confirm(`Deseja realmente excluir o documento "${documento.nome_arquivo}"?`)) {
      return;
    }

    try {
      await documentosAPI.deletar(documento.id);
      await carregarDocumentos();
      setToast({ message: 'Documento excluído com sucesso!', type: 'success' });
    } catch (error) {
      console.error('Erro ao excluir documento:', error);
      setToast({ message: 'Erro ao excluir documento', type: 'error' });
    }
  };

  if (!cliente) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-blue-600 text-white p-6 rounded-t-lg flex items-center justify-between sticky top-0">
          <div>
            <h2 className="text-2xl font-bold">Detalhes do Cliente</h2>
            <p className="text-blue-100 text-sm mt-1">{cliente.nome}</p>
          </div>
          <button
            onClick={onClose}
            className="hover:bg-blue-700 p-2 rounded-full transition duration-200"
          >
            <FaTimes size={20} />
          </button>
        </div>

        <div className="p-6">
          {/* Status Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center space-x-2">
              <FaInfoCircle className="text-blue-600" />
              <span>Status do Cliente</span>
            </h3>

            {/* Status Atual */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-600 font-medium mb-1">Status Atual</p>
              {loadingStatus ? (
                <FaSpinner className="animate-spin text-blue-600" />
              ) : (
                <p className={`text-lg ${statusAtual ? 'text-gray-800 font-semibold' : 'text-gray-400 italic'}`}>
                  {statusAtual || 'Sem status definido'}
                </p>
              )}
            </div>

            {/* Input novo status */}
            <div className="flex space-x-2 mb-4">
              <input
                type="text"
                value={novoStatus}
                onChange={(e) => setNovoStatus(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSalvarStatus()}
                placeholder="Digite o novo status..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={handleSalvarStatus}
                disabled={salvandoStatus || !novoStatus.trim()}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {salvandoStatus ? (
                  <FaSpinner className="animate-spin" />
                ) : (
                  <FaPaperPlane />
                )}
                <span>Atualizar</span>
              </button>
            </div>

            {/* Botão Histórico */}
            {historicoStatus.length > 1 && (
              <button
                onClick={() => setShowHistoricoModal(true)}
                className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 font-medium transition duration-200"
              >
                <FaHistory />
                <span>Ver histórico de alterações ({historicoStatus.length - 1} {historicoStatus.length - 1 === 1 ? 'anterior' : 'anteriores'})</span>
              </button>
            )}
          </div>

          {/* Divider */}
          <hr className="border-gray-300 mb-6" />

          {/* Upload Section */}
          <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-center mb-4">
              <FaUpload className="text-gray-400 text-3xl" />
            </div>

            <div className="text-center mb-4">
              <label
                htmlFor="file-upload"
                className="cursor-pointer inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition duration-200"
              >
                <FaFilePdf />
                <span>Selecionar PDFs</span>
              </label>
              <input
                id="file-upload"
                type="file"
                multiple
                accept="application/pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {selectedFiles.length > 0 && (
              <div className="space-y-3 mb-4">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="bg-white p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-3 mb-2">
                      <FaFilePdf className="text-red-600 text-xl" />
                      <span className="font-medium text-gray-800">{file.name}</span>
                      <span className="text-sm text-gray-500">
                        ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                    <input
                      type="text"
                      placeholder="Descrição (opcional)"
                      value={descricoes[index] || ''}
                      onChange={(e) => handleDescricaoChange(index, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                ))}

                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {uploading ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      <span>Enviando...</span>
                    </>
                  ) : (
                    <>
                      <FaUpload />
                      <span>Enviar Documentos</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Documents List */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Documentos Cadastrados ({documentos.length})
            </h3>

            {loading ? (
              <div className="text-center py-8">
                <FaSpinner className="animate-spin text-blue-600 text-3xl mx-auto" />
                <p className="text-gray-600 mt-2">Carregando documentos...</p>
              </div>
            ) : documentos.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FaFilePdf className="text-gray-300 text-5xl mx-auto mb-3" />
                <p>Nenhum documento cadastrado</p>
              </div>
            ) : (
              <div className="space-y-3">
                {documentos.map((doc) => (
                  <div
                    key={doc.id}
                    className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <FaFilePdf className="text-red-600 text-2xl mt-1" />
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-800">{doc.nome_arquivo}</h4>
                          {doc.descricao && (
                            <p className="text-sm text-gray-600 mt-1">{doc.descricao}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            Enviado em: {formatarData(doc.data_upload)}
                          </p>
                        </div>
                      </div>

                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => handleDownload(doc)}
                          className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition duration-200"
                          title="Download"
                        >
                          <FaDownload />
                        </button>
                        <button
                          onClick={() => handleDelete(doc)}
                          className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition duration-200"
                          title="Excluir"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-6 rounded-b-lg flex justify-end sticky bottom-0">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold rounded-lg transition duration-200"
          >
            Fechar
          </button>
        </div>
      </div>

      {/* Modal Histórico de Status */}
      {showHistoricoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="bg-blue-600 text-white p-4 rounded-t-lg flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FaHistory />
                <h3 className="text-lg font-bold">Histórico de Status</h3>
              </div>
              <button
                onClick={() => setShowHistoricoModal(false)}
                className="hover:bg-blue-700 p-2 rounded-full transition duration-200"
              >
                <FaTimes size={16} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 space-y-2">
              {historicoStatus.map((log, index) => (
                <div
                  key={log.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    index === 0
                      ? 'bg-blue-50 border-blue-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex-1">
                    <p className="text-sm text-gray-800">{log.status}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatarData(log.created_at)}
                      {index === 0 && (
                        <span className="ml-2 text-blue-600 font-medium">(atual)</span>
                      )}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeletarStatus(log)}
                    className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition duration-200 ml-3"
                    title="Excluir registro"
                  >
                    <FaTrash size={12} />
                  </button>
                </div>
              ))}
            </div>
            <div className="bg-gray-50 p-4 rounded-b-lg flex justify-end">
              <button
                onClick={() => setShowHistoricoModal(false)}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold rounded-lg transition duration-200"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default DocumentsModal;
